import type { DossierVu } from '../partage/api/types';
import { jourEtHeureDUnInstant } from '../partage/datesEnMots';
import { encartDeLaTuile, partFaite } from '../partage/lesProjets';
import { faitesSur, finAuPlusTot, finAuPlusTotDuCarnet, majuscule, PROCHAINE } from '../partage/vocabulaire';

/**
 * Ce que dit le carnet d'un projet sur l'étagère (chantier etagere-des-projets, maquette du
 * carnet `projets.html`, `.infos`), sans rendu. Rien n'y est recalculé : les phrases viennent de
 * ce qui les dit déjà (`encartDeLaTuile` du téléphone, `finAuPlusTot`, `faitesSur` de l'accueil).
 *
 * - « N faites sur M » et la part de la jauge, jamais écrite ;
 * - la suite : la prochaine chose de « Maintenant » (« Prochaine : … », qui s'ouvre), sinon le
 *   prochain rendez-vous (« Samedi 18 à 09:00 : … »), sinon la phrase calme de la tuile ;
 * - la dernière ligne : ce que le projet attend du monde, en ambre (jamais « en retard »), sinon
 *   sa fin au plus tôt.
 */
export interface InfosDuCarnet {
  faites: string;
  part: number;
  suite:
    | { sorte: 'chose'; etiquette: string; titre: string; continuer: string | null }
    | { sorte: 'phrase'; phrase: string }
    | null;
  fin: { monde: boolean; phrase: string };
}

export function infosDuCarnet(dossier: DossierVu): InfosDuCarnet {
  const { faites, total } = dossier.progression;
  // Ce que dirait la tuile sans prochaine chose : l'attente du monde, le rendez-vous, ou le calme.
  const sansProchaine = encartDeLaTuile({ ...dossier, prochaine: null });
  const rendezVous = dossier.faits.prochainRendezVous;

  let suite: InfosDuCarnet['suite'] = null;
  if (dossier.prochaine) {
    suite = { sorte: 'chose', etiquette: PROCHAINE, titre: dossier.prochaine.titre, continuer: dossier.prochaine.id };
  } else if (rendezVous) {
    const quand = majuscule(jourEtHeureDUnInstant(new Date(rendezVous.debut)));
    suite = { sorte: 'chose', etiquette: quand, titre: rendezVous.titre, continuer: null };
  } else if (sansProchaine.ton === 'calme') {
    suite = { sorte: 'phrase', phrase: sansProchaine.phrase };
  }

  const fin =
    sansProchaine.ton === 'monde'
      ? { monde: true, phrase: sansProchaine.phrase }
      : { monde: false, phrase: finAuPlusTotDuCarnet(finAuPlusTot(dossier.dateAuPlusTot)) };

  return { faites: faitesSur(faites, total), part: partFaite(faites, total), suite, fin };
}
